import type { PaginatedResponse } from "types/DataTypes";
import type { CloneReason } from "pages/metadoc/MetadocConstants";

import { api } from "./api";

type MetadocStatus = "pending" | "processing" | "completed" | "failed";

type SourceDocument = {
  id: number;
  name: string;
  page_count: number | null;
  file_size_bytes: number | null;
  created: string;
};

type Metadoc = {
  id: number;
  name: string;
  description: string;
  status: MetadocStatus;
  created: string;
  source_documents: SourceDocument[];
  file_size_bytes: number | null;
  page_count: number | null;
  analyst_page_count?: number | null;
  is_linearized: boolean;
  download_url: string | null;
  pre_ai_run_download_url: string | null;
  error_message: string | null;
  created_by: number;
  file: number;
  version_hash: string | null;
  clone_reason?: CloneReason | null;
};

type CreateMetadocArgs = {
  name: string;
  description?: string;
  file_id: number;
  document_ids: number[];
};

type GetMetadocStatusArgs = {
  metadocId: number;
};

type GetMetadocDownloadUrlArgs = {
  metadocId: number;
};

type MetadocStatusResponse = {
  status: MetadocStatus;
  is_ready: boolean;
  page_count: number | null;
  file_size_bytes: number | null;
  error_message?: string;
  concatenation_background_job?: {
    id: number;
    status: string;
    status_message: string;
    created: string;
    completion_time: string | null;
  };
};

type MetadocDownloadUrlResponse = {
  download_url: string;
};

type GetMetadocsForFileArgs = {
  fileId: string;
  limit?: string;
  offset?: string;
  searchTerm?: string;
};

type MetadocOption = {
  document_type: string;
  color: string;
  /** Sub-tag options may have no shortcut, since they're always assigned via the range picker. */
  shortcut: string | null;
};

export type GroupingSource = "OPS" | "AI";

export type MetadocMetadataType =
  | "summary"
  | "executive_summary"
  | "code"
  | "medication"
  | "other_summary"
  | "non_qualified_item";

export type MetadocMetadataOtherSummaryType =
  | "warning"
  | "cost_mitigation"
  | "current_status_of_treatment";

export type MetadocMetadataValue = Record<string, unknown> | string | null;

export type MetadocMetadata = {
  id: number;
  metadoc: number;
  document_group: number | null;
  type: MetadocMetadataType;
  code_type?: string | null;
  value: MetadocMetadataValue;
  date?: string | null;
  provider_name?: string | null;
  created: string;
  modified: string;
};

export type MetadocSummarySearchResult = {
  metadata_id: number;
  metadoc_id: number;
  document_group_id: number | null;
  page_number: number | null;
  tab_id: "executive-summary" | "per-record";
  snippet: string;
  matched_text: string;
};

export type MetadocSummarySearchResponse = {
  results: MetadocSummarySearchResult[];
  total_hits: number;
};

type DeletePagesArgs = {
  metadocId: number;
  pageRanges: number[][];
  versionHash: string;
  source: GroupingSource;
};

type DeletePagesResponse = {
  message: string;
  background_job: {
    id: number;
    status: string;
  };
};

type MovePagesArgs = {
  metadocId: number;
  sourcePageRanges: number[][];
  targetPosition: number;
  versionHash: string;
  source: GroupingSource;
};

type MovePagesResponse = {
  message: string;
  background_job: {
    id: number;
    status: string;
  };
};

type RunAIModelArgs = {
  metadocId: number;
};

type RunAIModelResponse = {
  message: string;
};

type GenerateCombinedPdfArgs = {
  metadocId: string;
};

type GenerateCombinedPdfResponse = {
  message: string;
  background_job: {
    id: number;
    status: string;
  };
};

type GenerateAllocatorNotesArgs = {
  metadocId: number;
};

type GenerateAllocatorNotesResponse = {
  message: string;
  background_job: {
    id: number;
    status: string;
  };
};

export type ExtractableDocumentType =
  | "RECORDS_FOR_RATED_AGE"
  | "FIRST_REPORT_OF_INJURY"
  | "MEDICAL_RECORDS_FOR_CMS_SUBMISSION";

type ExtractTaggedDocumentArgs = {
  metadocId: string;
  documentType: ExtractableDocumentType;
  // The future medical service (MSA or LCP/MCP) the user was viewing when
  // triggering the extraction, if any, so the resulting document's service
  // type isn't blank. At most one should be set.
  msaId?: string;
  costProjectionId?: string;
};

type ExtractTaggedDocumentResponse = {
  message: string;
  background_job: {
    id: number;
    status: string;
  };
};

type CloneMetadocArgs = {
  metadocId: number;
  cloneReason?: CloneReason;
  cloneName?: string;
};

type CloneMetadocResponse = {
  message: string;
  background_job: {
    id: number;
    status: string;
  };
};

type AddDocumentsToMetadocArgs = {
  metadocId: number;
  documentIds: number[];
  versionHash: string;
};

type AddDocumentsToMetadocResponse = {
  message: string;
  background_job: {
    id: number;
    status: string;
  };
};

type UpdateMetadocArgs = {
  metadocId: number;
  data: {
    name?: string;
    description?: string;
  };
};

type GetMetadocMetadataArgs = {
  metadocId: number;
  type?: MetadocMetadataType;
  _cacheVersion?: number; // Cache buster for tab switching
};

type UpdateMetadocMetadataArgs = {
  metadataId: number;
  data: {
    value?: MetadocMetadataValue;
    document_group?: number | null;
    date?: string | null;
    provider_name?: string | null;
  };
};

type CreateMetadocMetadataArgs = {
  data: {
    metadoc: number;
    type: MetadocMetadataType;
    value?: MetadocMetadataValue;
    document_group?: number;
    date?: string;
    provider_name?: string;
  };
};

type DeleteMetadocMetadataArgs = {
  metadataId: number;
};

type SearchMetadocSummaryArgs = {
  metadocId: number;
  query: string;
};

const metadocApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    // create a new metadoc
    createMetadoc: build.mutation<Metadoc, CreateMetadocArgs>({
      query: (data) => ({
        url: "api/v1/metadocs/",
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Metadocs", "Documents"],
    }),

    // get all metadocs for the organization
    getMetadocs: build.query<Metadoc[], void>({
      query: () => ({
        url: "api/v1/metadocs/",
        method: "GET",
      }),
      providesTags: ["Metadocs"],
    }),

    // get metadocs for a specific file with pagination
    getMetadocsForFile: build.query<
      PaginatedResponse<Metadoc>,
      GetMetadocsForFileArgs
    >({
      query: ({ fileId, limit, offset, searchTerm }) => ({
        url: "api/v1/metadocs/",
        method: "GET",
        params: {
          file_id: fileId,
          ...(limit && { limit }),
          ...(offset && { offset }),
          ...(searchTerm && { search: searchTerm }),
        },
      }),
      providesTags: ["Metadocs"],
    }),

    // get a specific metadoc
    getMetadoc: build.query<Metadoc, number>({
      query: (metadocId) => ({
        url: `api/v1/metadocs/${metadocId}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "Metadocs", id }],
    }),

    // get the status of a metadoc (with background job details)
    getMetadocStatus: build.query<MetadocStatusResponse, GetMetadocStatusArgs>({
      query: ({ metadocId }) => ({
        url: `api/v1/metadocs/${metadocId}/status/`,
        method: "GET",
      }),
      providesTags: (_result, _error, { metadocId }) => [
        { type: "Metadocs", id: metadocId },
      ],
    }),

    // get a fresh download URL for a metadoc
    getMetadocDownloadUrl: build.query<
      MetadocDownloadUrlResponse,
      GetMetadocDownloadUrlArgs
    >({
      query: ({ metadocId }) => ({
        url: `api/v1/metadocs/${metadocId}/download_url/`,
        method: "GET",
      }),
    }),

    // delete a metadoc
    deleteMetadoc: build.mutation<void, number>({
      query: (metadocId) => ({
        url: `api/v1/metadocs/${metadocId}/`,
        method: "DELETE",
      }),
      async onQueryStarted(metadocId, { dispatch, getState, queryFulfilled }) {
        const patchResults: Array<{ undo: () => void }> = [];
        const invalidatedQueries = api.util.selectInvalidatedBy(getState(), [
          { type: "Metadocs" },
        ]);

        invalidatedQueries.forEach(({ endpointName, originalArgs }) => {
          if (endpointName === "getMetadocs") {
            patchResults.push(
              dispatch(
                metadocApi.util.updateQueryData(
                  "getMetadocs",
                  undefined,
                  (draft) => {
                    const index = draft.findIndex(
                      (item) => item.id === metadocId,
                    );
                    if (index !== -1) {
                      draft.splice(index, 1);
                    }
                  },
                ),
              ),
            );
          }

          if (endpointName === "getMetadocsForFile") {
            patchResults.push(
              dispatch(
                metadocApi.util.updateQueryData(
                  "getMetadocsForFile",
                  originalArgs as GetMetadocsForFileArgs,
                  (draft) => {
                    const index = draft.results.findIndex(
                      (item) => item.id === metadocId,
                    );
                    if (index !== -1) {
                      draft.results.splice(index, 1);
                      draft.count = Math.max(draft.count - 1, 0);
                    }
                  },
                ),
              ),
            );
          }
        });

        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
      invalidatesTags: ["Metadocs", "Documents"],
    }),

    // Get metadoc sorting options.
    getMetadocOptions: build.query<MetadocOption[], void>({
      query: () => ({
        url: "api/v1/metadoc-options/",
        method: "GET",
      }),
    }),

    // get metadata rows for a metadoc, optionally filtered by metadata type
    getMetadocMetadata: build.query<MetadocMetadata[], GetMetadocMetadataArgs>({
      query: ({ metadocId, type }) => ({
        url: "api/v1/metadoc-metadata/",
        method: "GET",
        params: {
          metadoc: metadocId,
          ...(type && { type }),
        },
      }),
      transformResponse: (
        response: MetadocMetadata[] | PaginatedResponse<MetadocMetadata>,
      ) => (Array.isArray(response) ? response : (response.results ?? [])),
      providesTags: (result, _error, { metadocId }) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "MetadocMetadata" as const,
                id,
              })),
              { type: "MetadocMetadata", id: `METADOC-${metadocId}` },
            ]
          : [{ type: "MetadocMetadata", id: `METADOC-${metadocId}` }],
    }),

    searchMetadocSummary: build.mutation<
      MetadocSummarySearchResponse,
      SearchMetadocSummaryArgs
    >({
      query: ({ metadocId, query }) => ({
        url: "api/v1/metadoc-metadata/search-summary/",
        method: "POST",
        body: {
          metadoc_id: metadocId,
          query,
        },
      }),
    }),

    // delete pages from a metadoc
    deletePagesFromMetadoc: build.mutation<
      DeletePagesResponse,
      DeletePagesArgs
    >({
      query: ({ metadocId, pageRanges, versionHash, source }) => ({
        url: `api/v1/metadocs/${metadocId}/delete_pages/`,
        method: "POST",
        body: {
          page_ranges: pageRanges,
          version_hash: versionHash,
          source,
        },
      }),
      // the deletion hook invalidates tags after background job success
      invalidatesTags: [],
    }),

    // move pages within a metadoc
    movePagesWithinMetadoc: build.mutation<MovePagesResponse, MovePagesArgs>({
      query: ({
        metadocId,
        sourcePageRanges,
        targetPosition,
        versionHash,
        source,
      }) => ({
        url: `api/v1/metadocs/${metadocId}/move_pages/`,
        method: "POST",
        body: {
          source_page_ranges: sourcePageRanges,
          target_position: targetPosition,
          version_hash: versionHash,
          source,
        },
      }),
      // the movement hook invalidates metadoc tags after background job success
      invalidatesTags: [],
    }),

    // run AI model on a metadoc
    runAIModel: build.mutation<RunAIModelResponse, RunAIModelArgs>({
      query: ({ metadocId }) => ({
        url: `api/v1/metadocs/${metadocId}/ai-model-job/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { metadocId }) => [
        { type: "Metadocs", id: metadocId },
        "Metadocs",
        "BackgroundJobs",
      ],
    }),

    // generate combined PDF for a metadoc
    generateCombinedPdf: build.mutation<
      GenerateCombinedPdfResponse,
      GenerateCombinedPdfArgs
    >({
      query: ({ metadocId }) => ({
        url: `api/v1/metadocs/${metadocId}/generate-document/`,
        method: "POST",
        body: {
          document_type: "AI_SORTED_AND_CLASSIFIED_DOCUMENTS",
        },
      }),
      invalidatesTags: (_result, _error, { metadocId }) => [
        { type: "Metadocs", id: Number(metadocId) },
        "Metadocs",
        "BackgroundJobs",
        "Documents",
      ],
    }),

    // generate AI allocator notes for a metadoc
    generateAllocatorNotes: build.mutation<
      GenerateAllocatorNotesResponse,
      GenerateAllocatorNotesArgs
    >({
      query: ({ metadocId }) => ({
        url: `api/v1/metadocs/${metadocId}/generate-document/`,
        method: "POST",
        body: {
          document_type: "AI_ALLOCATOR_NOTES",
        },
      }),
      invalidatesTags: (_result, _error, { metadocId }) => [
        { type: "Metadocs", id: metadocId },
        "BackgroundJobs",
        "Documents",
      ],
    }),

    extractTaggedDocument: build.mutation<
      ExtractTaggedDocumentResponse,
      ExtractTaggedDocumentArgs
    >({
      query: ({ metadocId, documentType, msaId, costProjectionId }) => ({
        url: `api/v1/metadocs/${metadocId}/generate-document/`,
        method: "POST",
        body: {
          document_type: documentType,
          ...(msaId ? { msa: msaId } : {}),
          ...(costProjectionId ? { cost_projection: costProjectionId } : {}),
        },
      }),
      // No "Documents" tag here on purpose. The extract PDF doesn't exist
      // yet at dispatch time, only once the background job finishes, so
      // useDocumentGroupTagExtractionPolling invalidates it on completion
      // instead.
      invalidatesTags: (_result, _error, { metadocId }) => [
        { type: "Metadocs", id: Number(metadocId) },
        "BackgroundJobs",
      ],
    }),

    // clone a metadoc
    cloneMetadoc: build.mutation<CloneMetadocResponse, CloneMetadocArgs>({
      query: ({ metadocId, cloneReason, cloneName }) => ({
        url: `api/v1/metadocs/${metadocId}/clone/`,
        method: "POST",
        body: {
          ...(cloneReason ? { clone_reason: cloneReason } : {}),
          ...(cloneName ? { clone_name: cloneName } : {}),
        },
      }),
      invalidatesTags: ["Metadocs", "BackgroundJobs"],
    }),

    // append documents to an existing metadoc
    addDocumentsToMetadoc: build.mutation<
      AddDocumentsToMetadocResponse,
      AddDocumentsToMetadocArgs
    >({
      query: ({ metadocId, documentIds, versionHash }) => ({
        url: `api/v1/metadocs/${metadocId}/add-documents/`,
        method: "POST",
        body: {
          document_ids: documentIds,
          version_hash: versionHash,
        },
      }),
      invalidatesTags: (_result, _error, { metadocId }) => [
        { type: "Metadocs", id: metadocId },
        "Metadocs",
        "BackgroundJobs",
        "Documents",
      ],
    }),

    // update metadoc name and/or description
    updateMetadoc: build.mutation<Metadoc, UpdateMetadocArgs>({
      query: ({ metadocId, data }) => ({
        url: `api/v1/metadocs/${metadocId}/rename/`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: (_result, _error, { metadocId }) => [
        { type: "Metadocs", id: metadocId },
        "Metadocs",
      ],
    }),

    // update metadoc metadata
    updateMetadocMetadata: build.mutation<
      MetadocMetadata,
      UpdateMetadocMetadataArgs
    >({
      query: ({ metadataId, data }) => ({
        url: `api/v1/metadoc-metadata/${metadataId}/`,
        method: "PATCH",
        body: data,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: (_result, _error, { metadataId }) => [
        { type: "MetadocMetadata", id: metadataId },
      ],
    }),

    // create a metadoc metadata row
    createMetadocMetadata: build.mutation<
      MetadocMetadata,
      CreateMetadocMetadataArgs
    >({
      query: ({ data }) => ({
        url: "api/v1/metadoc-metadata/",
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: (_result, _error, { data: { metadoc } }) => [
        { type: "MetadocMetadata", id: `METADOC-${metadoc}` },
      ],
    }),

    // delete a metadoc metadata row
    deleteMetadocMetadata: build.mutation<void, DeleteMetadocMetadataArgs>({
      query: ({ metadataId }) => ({
        url: `api/v1/metadoc-metadata/${metadataId}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { metadataId }) => [
        { type: "MetadocMetadata", id: metadataId },
      ],
    }),
  }),
});

export const {
  useCreateMetadocMutation,
  useGetMetadocsQuery,
  useGetMetadocsForFileQuery,
  useGetMetadocQuery,
  useGetMetadocStatusQuery,
  useGetMetadocDownloadUrlQuery,
  useDeleteMetadocMutation,
  useGetMetadocOptionsQuery,
  useGetMetadocMetadataQuery,
  useDeletePagesFromMetadocMutation,
  useMovePagesWithinMetadocMutation,
  useRunAIModelMutation,
  useGenerateCombinedPdfMutation,
  useExtractTaggedDocumentMutation,
  useCloneMetadocMutation,
  useAddDocumentsToMetadocMutation,
  useUpdateMetadocMutation,
  useUpdateMetadocMetadataMutation,
  useCreateMetadocMetadataMutation,
  useDeleteMetadocMetadataMutation,
  useSearchMetadocSummaryMutation,
  useGenerateAllocatorNotesMutation,
} = metadocApi;

export default metadocApi;

export type { Metadoc, MetadocOption, MetadocStatus, MetadocStatusResponse };
